import {
  ActionIcon,
  Box,
  Center,
  ComboboxItem,
  Loader,
  LoadingOverlay,
  NumberFormatter,
  NumberInput,
  rem,
  Select,
  Table,
  Text,
  Accordion
} from '@mantine/core';
import { errorBoundaryWrapper, useAgLazyQuery } from '@repo/utils';
import React, { memo, useMemo, useState } from 'react';
import { IconCircleCheck, IconCirclePlus, IconCircleX, IconEdit, IconTrash } from '@tabler/icons-react';
import {
  Property,
  PropertyPrice,
  NewPropertyPriceVm,
  useTransactionPriceTypesQuery,
  useLazyPropertyPricesByPropertyIdQuery,
  useAddPropertyPricesMutation,
  useUpdatePropertyPriceMutation,
  useDeletePropertyPriceMutation,
  useLazyGetPropertiesByTransactionIdQuery
} from '@repo/services';

interface PropertyPriceDetailsProps {
  transactionId: number | undefined;
  canEdit?: boolean;
}

const PropertyPriceRow = memo(({
  propertyPrice,
  propertyId,
  isNew = false,
  canEdit = true,
  existingPriceTypeIds = [],
  onSave,
  onCancel,
  onDelete
}: {
  propertyPrice?: PropertyPrice;
  propertyId: number;
  isNew?: boolean;
  canEdit?: boolean;
  existingPriceTypeIds?: number[];
  onSave: (price: PropertyPrice | NewPropertyPriceVm) => void;
  onCancel?: () => void;
  onDelete?: (id: number) => void;
}) => {
  const { data: transactionPriceTypes = [] } = useTransactionPriceTypesQuery(null);
  const [isEdit, setIsEdit] = useState(isNew);
  const [transactionPriceTypeId, setTransactionPriceTypeId] = useState<number>(propertyPrice?.transactionPriceTypeId || 0);
  const [amount, setAmount] = useState<number>(propertyPrice?.amount || 0);

  const selectListData = useMemo(() => {
    return transactionPriceTypes
      ?.filter((tpt) => !existingPriceTypeIds.includes(tpt.id) || tpt.id === propertyPrice?.transactionPriceTypeId)
      ?.map((tpt) => ({
        value: tpt.id?.toString(),
        label: tpt.name
      }));
  }, [transactionPriceTypes, existingPriceTypeIds, propertyPrice?.transactionPriceTypeId]);

  const handleSelect = (option: ComboboxItem) => {
    setTransactionPriceTypeId(Number(option.value));
  };

  const handleSave = () => {
    if (propertyPrice?.id) {
      // Update existing
      onSave({
        ...propertyPrice,
        transactionPriceTypeId,
        amount
      });
    } else {
      // Create new
      onSave({
        propertyId,
        transactionPriceTypeId,
        amount
      });
    }
    if (!isNew) setIsEdit(false);
  };

  const handleCancelEdit = () => {
    if (isNew && onCancel) {
      onCancel();
    } else {
      setTransactionPriceTypeId(propertyPrice?.transactionPriceTypeId || 0);
      setAmount(propertyPrice?.amount || 0);
      setIsEdit(false);
    }
  };

  return (
    <Table.Tr>
      <Table.Td>
        {isEdit && canEdit ? (
          <Select
            data={selectListData}
            placeholder={selectListData?.length > 0 ? 'Price Type' : 'No Price Types Available'}
            value={transactionPriceTypeId ? transactionPriceTypeId.toString() : null}
            onChange={(_value, option) => handleSelect(option)}
          />
        ) : (
          <>{transactionPriceTypes?.find((pt) => pt.id === propertyPrice?.transactionPriceTypeId)?.name || ''}</>
        )}
      </Table.Td>
      <Table.Td>
        {isEdit && canEdit ? (
          <NumberInput prefix={'$'} value={amount} onChange={(val) => setAmount(Number(val))} />
        ) : (
          <NumberFormatter value={propertyPrice?.amount || 0} thousandSeparator prefix={'$'} />
        )}
      </Table.Td>
      <Table.Td>
        {canEdit && (
          <>
            {isNew ? (
              <ActionIcon.Group>
                <ActionIcon onClick={handleSave} color='green' size='lg' h={36} w={50} aria-label='Submit Price' disabled={!transactionPriceTypeId}>
                  <IconCirclePlus style={{ width: rem(20) }} stroke={1.5} />
                </ActionIcon>
                {onCancel && (
                  <ActionIcon onClick={onCancel} variant='default' size='lg' h={36} w={50} aria-label='Cancel'>
                    <IconCircleX style={{ width: rem(20) }} stroke={1.5} />
                  </ActionIcon>
                )}
              </ActionIcon.Group>
            ) : (
              <ActionIcon.Group>
                {isEdit ? (
                  <>
                    <ActionIcon onClick={handleCancelEdit} variant='default' size='lg' h={36} w={50} aria-label='Cancel'>
                      <IconCircleX style={{ width: rem(20) }} stroke={1.5} />
                    </ActionIcon>
                    <ActionIcon onClick={handleSave} color='green' size='lg' h={36} w={50} aria-label='Save'>
                      <IconCircleCheck style={{ width: rem(20) }} stroke={1.5} />
                    </ActionIcon>
                  </>
                ) : (
                  <>
                    <ActionIcon onClick={() => setIsEdit(true)} size='lg' color='blue' h={36} w={50} aria-label='Edit'>
                      <IconEdit style={{ width: rem(20) }} stroke={1.5} />
                    </ActionIcon>
                    <ActionIcon onClick={() => onDelete?.(propertyPrice?.id || 0)} size='lg' color='red' h={36} w={50} aria-label='Delete'>
                      <IconTrash style={{ width: rem(20) }} stroke={1.5} />
                    </ActionIcon>
                  </>
                )}
              </ActionIcon.Group>
            )}
          </>
        )}
      </Table.Td>
    </Table.Tr>
  );
});

const PropertyPriceSection = memo(({
  property,
  canEdit = true
}: {
  property: Property;
  canEdit?: boolean;
}) => {
  const [propertyPrices, propertyPricesLoading] = useAgLazyQuery<PropertyPrice[]>(
    property?.id?.toString(),
    useLazyPropertyPricesByPropertyIdQuery
  );

  const [addPropertyPrices] = useAddPropertyPricesMutation();
  const [updatePropertyPrice] = useUpdatePropertyPriceMutation();
  const [deletePropertyPrice] = useDeletePropertyPriceMutation();

  const [showNewRow, setShowNewRow] = useState(false);

  const existingPriceTypeIds = useMemo(() => {
    return propertyPrices?.map(p => p.transactionPriceTypeId) || [];
  }, [propertyPrices]);

  const handleSave = async (price: PropertyPrice | NewPropertyPriceVm) => {
    if ('id' in price && price.id) {
      await updatePropertyPrice(price as PropertyPrice);
    } else {
      await addPropertyPrices([price as NewPropertyPriceVm]);
    }
    setShowNewRow(false);
  };

  const handleDelete = async (id: number) => {
    await deletePropertyPrice(id);
  };

  const totalPrice = useMemo(() => {
    return propertyPrices?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
  }, [propertyPrices]);

  return (
    <Box mb="md">
      <Text fw={500} mb="xs">{property.title}</Text>
      <LoadingOverlay visible={propertyPricesLoading} />
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Price Type</Table.Th>
            <Table.Th>Amount</Table.Th>
            {canEdit && <Table.Th>Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {canEdit && showNewRow && (
            <PropertyPriceRow
              propertyId={property.id}
              isNew
              canEdit={canEdit}
              existingPriceTypeIds={existingPriceTypeIds}
              onSave={handleSave}
              onCancel={() => setShowNewRow(false)}
            />
          )}
          {canEdit && !showNewRow && (
            <Table.Tr>
              <Table.Td colSpan={3}>
                <ActionIcon onClick={() => setShowNewRow(true)} color='blue' size='lg' aria-label='Add Price'>
                  <IconCirclePlus style={{ width: rem(20) }} stroke={1.5} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          )}
          {propertyPrices?.map((price) => (
            <PropertyPriceRow
              key={price.id}
              propertyPrice={price}
              propertyId={property.id}
              canEdit={canEdit}
              existingPriceTypeIds={existingPriceTypeIds}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          ))}
          <Table.Tr>
            <Table.Td><Text fw='bold' size='sm'>Subtotal</Text></Table.Td>
            <Table.Td><Text fw='bold' size='sm'><NumberFormatter value={totalPrice} thousandSeparator prefix='$' /></Text></Table.Td>
            {canEdit && <Table.Td />}
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Box>
  );
});

const TransactionPriceDetails = memo(({ transactionId, canEdit = true }: PropertyPriceDetailsProps) => {
  const [properties, propertiesLoading] = useAgLazyQuery<Property[]>(
    transactionId,
    useLazyGetPropertiesByTransactionIdQuery
  );

  const totalEstPrice = useMemo(() => {
    if (!properties) return 0;
    return properties.reduce((total, property) => {
      const propertyTotal = property.propertyPrices?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
      return total + propertyTotal;
    }, 0);
  }, [properties]);

  if (propertiesLoading) {
    return (
      <Center h={100}>
        <Loader size="sm" />
        <Text ml="sm">Loading price details...</Text>
      </Center>
    );
  }

  if (!properties || properties.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="md">
        No properties found for this transaction
      </Text>
    );
  }

  return (
    <Box>
      {properties.length === 1 && properties[0] ? (
        <PropertyPriceSection property={properties[0]} canEdit={canEdit} />
      ) : (
        <Accordion variant="contained" defaultValue={properties[0]?.id?.toString()}>
          {properties.map((property) => (
            <Accordion.Item key={property.id} value={property.id.toString()}>
              <Accordion.Control>{property.title}</Accordion.Control>
              <Accordion.Panel>
                <PropertyPriceSection property={property} canEdit={canEdit} />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {properties.length > 1 && (
        <Box mt="md" p="sm" style={{ borderTop: '2px solid var(--mantine-color-gray-3)' }}>
          <Table>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td><Text fw='bold'>Total Estimated Price</Text></Table.Td>
                <Table.Td><Text fw='bold'><NumberFormatter value={totalEstPrice} thousandSeparator prefix='$' /></Text></Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
});

export default errorBoundaryWrapper(TransactionPriceDetails);



